/**
 * Email utility functions for parsing and extracting metadata from email HTML.
 */

/**
 * Extracts preheader text from email HTML.
 * Preheaders are hidden elements that email clients display as preview text.
 * 
 * Common hiding patterns detected:
 * - display:none or display:none!important
 * - max-height:0px combined with overflow:hidden
 * - opacity:0 combined with overflow:hidden
 * - font-size:1px combined with line-height:1px
 */
export const extractPreheader = (html: string): string | null => {
  if (!html) return null;
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Find elements with hiding styles
  const elementsWithStyle = doc.querySelectorAll('[style]');
  
  for (const el of elementsWithStyle) {
    const style = el.getAttribute('style')?.toLowerCase() || '';
    
    // Check for common preheader hiding patterns
    const isHidden = 
      style.includes('display:none') ||
      style.includes('display: none') ||
      (style.includes('max-height:0') && style.includes('overflow:hidden')) ||
      (style.includes('opacity:0') && style.includes('overflow:hidden')) ||
      (style.includes('font-size:1px') && style.includes('line-height:1px') && style.includes('max-height:0'));
    
    if (isHidden) {
      // Get text and clean it
      let text = el.textContent || '';
      
      // Remove padding characters (invisible Unicode, &shy;, etc.)
      text = text
        .replace(/\u034F/g, '')      // Combining grapheme joiner
        .replace(/\u200B/g, '')      // Zero-width space
        .replace(/\u200C/g, '')      // Zero-width non-joiner
        .replace(/\u200D/g, '')      // Zero-width joiner
        .replace(/\u00AD/g, '')      // Soft hyphen (&shy;)
        .replace(/\uFEFF/g, '')      // Zero-width no-break space
        .replace(/͏/g, '')           // Combining character (U+034F)
        .replace(/&shy;/gi, '')      // HTML soft hyphen entity
        .replace(/\s+/g, ' ')        // Collapse whitespace
        .trim();
      
      // Return if we have meaningful text (more than just punctuation/spaces)
      if (text.length > 5) {
        return text;
      }
    }
  }
  
  return null;
};
